import { useState, type FormEvent, type KeyboardEvent } from 'react';
import type { SessionMembership, SessionUser } from '@azko/shared';
import { ApiRequestError, api, setActiveCondominium } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { InstallAppButton } from '../pwa/pwa.js';
import {
  Alert,
  Button,
  Field,
  MadeByAzkoGroup,
  PasswordInput,
  TextInput,
} from '../components/ui.js';
import { IconBarcode, IconPackage, IconSparkle } from '../components/icons.js';
import { CondosMark, CondosWordmark } from '../components/brand.js';

interface LoginResponse {
  token: string;
  user: SessionUser;
  memberships: SessionMembership[];
}

/** Credencial de demonstracao: existe so no bundle de dev. */
const DEMO = { email: 'carlos@azko.app', password: 'azko1234' };

const HIGHLIGHTS = [
  {
    icon: IconBarcode,
    title: 'Entrada sem digitar',
    text: 'A camera le a etiqueta e preenche unidade, transportadora e rastreio.',
  },
  {
    icon: IconPackage,
    title: 'Morador avisado na hora',
    text: 'Notificacao com foto e QR de retirada assim que a encomenda entra.',
  },
  {
    icon: IconSparkle,
    title: 'Um sistema, varios modulos',
    text: 'Encomendas hoje; visitantes, reservas e financeiro no mesmo lugar.',
  },
];

/**
 * Entrada do produto.
 *
 * Divide a tela em dois papeis: a esquerda vende (e a unica superficie que um
 * sindico ve antes de assinar), a direita trabalha. No mobile a coluna de
 * venda sai inteira — quem abre o app no celular e o porteiro, que ja comprou
 * e so quer entrar.
 */
/**
 * Pedir o link de recuperacao.
 *
 * A confirmacao NAO diz se o e-mail existe, e a API responde igual nos dois
 * casos. "Nao encontramos esse e-mail" numa tela publica transforma o
 * formulario num verificador de cadastro: com uma lista de enderecos, qualquer
 * um descobre quem e cliente da Azko.
 *
 * Por isso o texto de sucesso fala do QUE FAZER a seguir, e nao do que
 * aconteceu: "se existir conta, o link chega" e a unica frase honesta que nao
 * entrega nada.
 */
function RecuperarSenha() {
  const [aberto, setAberto] = useState(false);
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="text-xs font-bold text-muted transition hover:text-fg"
      >
        Esqueci minha senha
      </button>
    );
  }

  if (enviado) {
    return (
      <p className="text-xs leading-relaxed text-muted">
        Se existir uma conta com esse e-mail, o link para escolher uma senha nova
        chega em alguns minutos. Ele vale por uma hora. Confira tambem o spam.
      </p>
    );
  }

  async function pedir(event: FormEvent) {
    event.preventDefault();
    setEnviando(true);
    try {
      await api.post('/auth/recuperar', { email: email.trim() });
    } catch {
      // De proposito sem tratar: a tela responde igual para sucesso e para
      // falha, senao o erro vira o canal que conta se o e-mail existe.
    } finally {
      setEnviando(false);
      setEnviado(true);
    }
  }

  return (
    <form onSubmit={pedir} className="space-y-3">
      <Field label="E-mail da conta">
        <TextInput
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          inputMode="email"
          spellCheck={false}
          placeholder="voce@condominio.com.br"
          autoFocus
          required
        />
      </Field>
      <Button type="submit" variant="ghost" className="w-full" loading={enviando} disabled={!email}>
        Enviar link de recuperacao
      </Button>
    </form>
  );
}

export function LoginPage() {
  const { refresh } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Segundo fator. `false` ate a API dizer que aquela conta usa.
   *
   * A tela nao pergunta o codigo de saida porque isso contaria, a quem so tem o
   * e-mail, quais contas tem 2FA — e nao ha razao para dar essa lista. O campo
   * aparece depois da senha certa, que e quando a pergunta faz sentido.
   */
  const [totp, setTotp] = useState(false);
  const [code, setCode] = useState('');

  async function submit(credentials: { email: string; password: string }) {
    setError(null);
    setSubmitting(true);

    try {
      const result = await api.post<LoginResponse>('/auth/login', {
        // O porteiro digita no tablet e o teclado capitaliza a primeira letra
        // sozinho; sem normalizar, "Carlos@..." vira credencial invalida.
        email: credentials.email.trim().toLowerCase(),
        password: credentials.password,
        ...(code.trim() ? { totpCode: code.trim() } : {}),
      });
      // Usuario com um condominio so ja entra nele; com varios, a troca de
      // contexto fica disponivel no shell.
      //
      // Sem membership (equipe Azko), LIMPA o condominio ativo: senao o id que
      // sobrou do login anterior ia no header X-Condominium e a API respondia
      // 403 "voce nao tem acesso a este condominio" — o que parecia erro de
      // senha e jogava o admin de volta para esta tela.
      const first = result.memberships[0];
      setActiveCondominium(first ? first.condominium.id : null);
      refresh();
    } catch (err) {
      const apiError = err instanceof ApiRequestError ? err : null;

      // `totp_required` nao e falha: e a senha aceita e a conta pedindo a
      // segunda metade. Mostrar "nao foi possivel entrar" aqui faria a pessoa
      // duvidar da senha que ela acabou de acertar.
      if (apiError?.code === 'totp_required') {
        setTotp(true);
        setError(null);
      } else {
        if (apiError?.code === 'totp_invalid') setCode('');
        setError(apiError ? apiError.message : 'Nao foi possivel entrar. Tente novamente.');
      }

      setSubmitting(false);
    }
    // Sem `finally`: no sucesso a sessao troca a tela inteira e voltar o botao
    // ao estado normal so produz um piscar de "Entrar" antes de desmontar.
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void submit({ email, password });
  }

  // Erro de senha em caixa alta e o suporte numero um de portaria.
  function trackCapsLock(event: KeyboardEvent<HTMLInputElement>) {
    setCapsLock(event.getModifierState?.('CapsLock') ?? false);
  }

  return (
    <div className="safe-area-all safe-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Coluna de marca — desktop */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-line-soft bg-sidebar p-12 lg:flex xl:p-16">
        <span
          aria-hidden
          className="pointer-events-none absolute -left-40 -top-40 size-152 rounded-full bg-accent/8 blur-3xl"
        />

        <div className="relative flex items-center gap-3.5">
          <span className="grid size-11 place-items-center rounded-2xl bg-accent text-on-accent">
            <CondosMark className="h-6 w-5.5" />
          </span>
          <span>
            <CondosWordmark height="1.15rem" />
            <span className="mt-1 block text-xs text-muted">Gestao de condominios</span>
          </span>
        </div>

        <div className="relative max-w-lg">
          <p className="eyebrow">Portaria conectada</p>
          <h2 className="display-title mt-3">
            A portaria para
            <br />
            de digitar.
          </h2>
          <p className="mt-4 text-muted">
            Encomenda registrada pela camera, morador notificado na hora e retirada confirmada com
            um QR de uso unico.
          </p>

          <ul className="mt-10 space-y-5">
            {HIGHLIGHTS.map((item) => (
              <li key={item.title} className="flex gap-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-line bg-surface text-accent">
                  <item.icon className="size-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold">{item.title}</span>
                  <span className="mt-0.5 block text-sm text-muted">{item.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative">
          <p className="text-xs text-faint">
            © {new Date().getFullYear()} Azko Condos — plataforma de gestao de condominios
          </p>
          <MadeByAzkoGroup className="mt-1" />
        </div>
      </aside>

      {/* Coluna do formulario */}
      <main className="flex items-center justify-center px-5 py-12 sm:px-10">
        <div className="w-full max-w-sm">
          {/* Logo no mobile: sem a coluna da esquerda, a tela ficaria sem marca */}
          <div className="mb-8 flex items-center gap-3.5 lg:hidden">
            <span className="grid size-11 place-items-center rounded-2xl bg-accent text-on-accent">
              <CondosMark className="h-6 w-5.5" />
            </span>
            <CondosWordmark height="1.15rem" />
          </div>

          <p className="eyebrow">Acesso</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Entrar.</h1>
          <p className="mt-2 text-sm text-muted">Portaria e administracao do condominio.</p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
            <Field label="E-mail">
              <TextInput
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                inputMode="email"
                spellCheck={false}
                placeholder="voce@condominio.com.br"
                aria-invalid={error ? true : undefined}
                required
              />
            </Field>

            <Field label="Senha">
              <PasswordInput
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyUp={trackCapsLock}
                onKeyDown={trackCapsLock}
                autoComplete="current-password"
                placeholder="••••••••"
                aria-invalid={error ? true : undefined}
                required
              />
            </Field>

            {totp && (
              <Field label="Codigo do aplicativo">
                <TextInput
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  // `one-time-code` faz o iOS oferecer o codigo do teclado, e
                  // `inputMode` abre o teclado numerico — os dois importam para
                  // quem esta com o telefone na mao e o app aberto ao lado.
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={20}
                  autoFocus
                  required
                />
              </Field>
            )}

            {totp && (
              <p className="text-xs leading-relaxed text-faint">
                Nao esta com o aparelho? Use um dos codigos de recuperacao que voce
                guardou ao ativar.
              </p>
            )}

            {capsLock && <p className="text-xs font-semibold text-warn">Caps Lock esta ligado.</p>}

            {error && <Alert>{error}</Alert>}

            <Button
              type="submit"
              className="w-full"
              loading={submitting}
              disabled={!email || !password || (totp && !code)}
            >
              {submitting ? 'Entrando...' : totp ? 'Confirmar codigo' : 'Entrar'}
            </Button>
          </form>

          {import.meta.env.DEV && (
            <div className="mt-8 rounded-2xl border border-line bg-surface p-4">
              <p className="text-xs font-semibold text-muted">Ambiente de desenvolvimento</p>
              <div className="mt-2 flex items-center justify-between gap-3">
                <code className="min-w-0 truncate text-xs text-faint">{DEMO.email}</code>
                <button
                  type="button"
                  onClick={() => {
                    setEmail(DEMO.email);
                    setPassword(DEMO.password);
                    void submit(DEMO);
                  }}
                  className="shrink-0 text-xs font-bold text-accent hover:underline"
                >
                  Entrar como demo
                </button>
              </div>
            </div>
          )}

          {/* A frase antiga aqui era "fale com o sindico ou a administradora".
              Ela descrevia o produto com honestidade e era o maior custo de
              suporte que ele tinha: toda senha esquecida, em qualquer hora,
              virava um contato humano — e a do proprio sindico terminava na
              Azko. */}
          <div className="mt-8">
            <RecuperarSenha />
          </div>

          <InstallAppButton className="mt-6" />

          {/* No mobile a coluna da esquerda nao existe, entao a assinatura
              apareceria em lugar nenhum. */}
          <MadeByAzkoGroup className="mt-6 lg:hidden" />
        </div>
      </main>
    </div>
  );
}
